import {
  User,
  LoginResponse,
  RegisterResponse,
  IdentityVerificationResponse,
  SensitiveProfileResponse,
} from '../../types/auth'
import { apiRequest, setAccessToken, clearTokens } from './client'

interface RegisterData {
  email: string
  password: string
  firstName?: string
  lastName?: string
  phone?: string
  address?: {
    city?: string
    state?: string
  }
}

interface IdentityVerificationData {
  ssn?: string
  dateOfBirth?: string
  addressStreet?: string
  addressZip?: string
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const data = await apiRequest<{ token: string; refreshToken: string; user: User }>(
      '/auth/login',
      { method: 'POST', body: { email, password }, auth: false },
    )

    setAccessToken(data.token)
    if (data.refreshToken) {
      localStorage.setItem('refresh_token', data.refreshToken)
    }

    return { token: data.token, user: data.user }
  },

  async register(userData: RegisterData): Promise<RegisterResponse> {
    const data = await apiRequest<{ token: string; refreshToken: string; user: User }>(
      '/auth/register',
      { method: 'POST', body: userData, auth: false },
    )

    setAccessToken(data.token)
    if (data.refreshToken) {
      localStorage.setItem('refresh_token', data.refreshToken)
    }

    return { token: data.token, user: data.user }
  },

  async getCurrentUser(): Promise<User> {
    const data = await apiRequest<{ user: User }>('/auth/me')
    return data.user
  },

  async verifyIdentity(verificationData: IdentityVerificationData): Promise<IdentityVerificationResponse> {
    const data = await apiRequest<{ user: User; verificationStatus: string }>(
      '/auth/verify-identity',
      { method: 'POST', body: verificationData },
    )

    return {
      user: data.user,
      verificationStatus: data.verificationStatus as 'pending' | 'approved' | 'rejected',
    }
  },

  async getSensitiveProfile(): Promise<SensitiveProfileResponse> {
    return apiRequest<SensitiveProfileResponse>('/profile/sensitive')
  },

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const data = await apiRequest<{ user: User }>(
      '/auth/profile',
      { method: 'PATCH', body: profileData },
    )
    return data.user
  },

  async forgotPassword(email: string): Promise<void> {
    await apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: { email },
      auth: false,
    })
  },

  async resetPassword(accessToken: string, newPassword: string): Promise<void> {
    await apiRequest('/auth/reset-password', {
      method: 'POST',
      body: { accessToken, newPassword },
      auth: false,
    })
  },

  async changePassword(_currentPassword: string, newPassword: string): Promise<void> {
    await apiRequest('/auth/change-password', {
      method: 'POST',
      body: { currentPassword: _currentPassword, newPassword },
    })
  },

  async logout(): Promise<void> {
    await Promise.allSettled([
      apiRequest('/auth/logout', { method: 'POST' }),
    ])
    clearTokens()
  },

  async resetDemo(): Promise<{ success: boolean; user: User }> {
    const data = await apiRequest<{ success: boolean; user: User }>(
      '/auth/reset-demo',
      { method: 'POST' },
    )
    return data
  },
}
